import React from 'react';
import { Formik } from 'formik';
import {
  Autocomplete,
  MealProvisionOptionType,
  theme,
} from '@fdha/web-ui-library';
import { Box, Checkbox, Chip } from '@mui/material';

import { EditableCard, EditableCardProps } from './EditableCard';

// Backend mirror: packages/backend/src/db/foodProgramV2/mealProvisionOptionsValidation.ts
// Keep the two copies in sync. Rule changes rarely; ~5 lines of logic.
const validateMpoSelection = (
  value: { id: MealProvisionOptionType }[]
): string | null => {
  if (value.length === 0) {
    return 'At least one meal provision option must be selected.';
  }
  const ids = new Set(value.map((v) => v.id));
  if (
    ids.has(MealProvisionOptionType.MealKit) &&
    !ids.has(MealProvisionOptionType.FaethProvided)
  ) {
    return 'Meal Kits require Faeth-provided to also be selected.';
  }
  return null;
};

type MpoFormValues = {
  mealProvisionOptions: { id: MealProvisionOptionType; label: string }[];
};

export interface EditableCardMultiSelectProps extends EditableCardProps {
  initialValue: { id: MealProvisionOptionType; label: string }[];
  onSave: (value: { id: MealProvisionOptionType }[]) => Promise<void>;
  selectProps: { id: MealProvisionOptionType; label: string }[];
}

export const EditableCardMultiSelect: React.FC<
  EditableCardMultiSelectProps
> = ({ initialValue, onSave, selectProps, ...editableCardProps }) => {
  const handleSubmit = async (values: MpoFormValues) => {
    const err = validateMpoSelection(values.mealProvisionOptions ?? []);
    if (err) {
      // Formik's validate hook will already show the inline error; this guard
      // prevents the API call from firing if a user somehow clicks save before
      // validation has propagated (race / off-by-one render).
      return;
    }
    await onSave(values.mealProvisionOptions);
  };
  return (
    <Formik
      enableReinitialize
      validateOnMount
      initialValues={{ mealProvisionOptions: initialValue }}
      validate={(values: MpoFormValues) => {
        const errors: Record<string, string> = {};
        const err = validateMpoSelection(values.mealProvisionOptions ?? []);
        if (err) errors.mealProvisionOptions = err;
        return errors;
      }}
      onSubmit={handleSubmit}
    >
      {({ setFieldValue, values, errors }) => {
        return (
          <EditableCard {...editableCardProps} disableSaveOnInvalid>
            <Autocomplete
              multiple
              fullWidth
              // Controlled by Formik so the chips stay in sync with
              // values.mealProvisionOptions. An uncontrolled defaultValue only
              // reads on mount, so after enableReinitialize or a cancel/reopen
              // the visible chips would drift from the Formik state being saved.
              value={values.mealProvisionOptions}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              ChipProps={{
                sx: {
                  backgroundColor: theme.palette.grey[300],
                  borderRadius: '20px',
                  padding: '4px 8px',
                  margin: '2px',
                },
              }}
              options={selectProps}
              loading={false}
              renderOption={(props, option, { selected }) => (
                <li {...props}>
                  <Checkbox checked={selected} style={{ marginRight: 8 }} />
                  {option.label}
                </li>
              )}
              renderTags={(tagValue, getTagProps) =>
                tagValue.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    label={option.label}
                    sx={{
                      backgroundColor: theme.palette.grey[300],
                      borderRadius: '20px',
                      padding: '4px 8px',
                      margin: '2px',
                    }}
                  />
                ))
              }
              onChange={(_event, value) => {
                setFieldValue('mealProvisionOptions', value);
              }}
              sx={{
                width: '120%',
              }}
            />
            {errors.mealProvisionOptions && (
              <Box
                sx={{
                  color: theme.palette.error.main,
                  fontSize: '0.75rem',
                  mt: 0.5,
                }}
              >
                {errors.mealProvisionOptions as string}
              </Box>
            )}
            {!values.mealProvisionOptions?.some(
              (opt: { id: MealProvisionOptionType }) =>
                opt.id === MealProvisionOptionType.FaethProvided
            ) && (
              <Box
                sx={{
                  color: theme.palette.text.secondary,
                  fontSize: '0.75rem',
                  mt: 0.5,
                }}
              >
                Existing scheduled deliveries are not cancelled automatically.
                Cancel them manually from the Deliveries tab.
              </Box>
            )}
          </EditableCard>
        );
      }}
    </Formik>
  );
};

export default EditableCardMultiSelect;
